/**
 * Readiness analytical overview — converged (ADR-0007 / 2026-05-27 review).
 *
 * The five-way readiness numbers live in the top ReadinessHeader ribbon and
 * the applicable / hidden / needs-config counts live in the Scope strip, so
 * this panel no longer repeats them. What remains here is the non-duplicated
 * analysis:
 *   - count reconciliation (applicable = hard_law + evidence + market +
 *     ecosystem; visible = applicable - placeholder), the honest explanation
 *     of why the board shows fewer cards than the readiness counters;
 *   - signal-impact audit;
 *   - the Lifecycle x Timing heatmap (the one 2-D view, not duplicated).
 *
 * Lesson §25.7: the five readiness tracks are never summed; nothing here
 * produces an aggregate score.
 */

import type { ComplianceRequirementCard } from "@regpulse/contracts";
import type { EvaluateResponse } from "../services/api";
import { LifecycleTimingHeatmap } from "./LifecycleTimingHeatmap";
import { summarizeReadinessDelta, type ReadinessDelta } from "../lib/readiness-delta";

interface Props {
  evaluation: EvaluateResponse;
  cards: ComplianceRequirementCard[];
  /** Audit G9 — per-track readiness movement vs the previous evaluation. Null
   *  until a baseline exists (first evaluation). */
  delta?: ReadinessDelta | null;
}

export function ReadinessOverview({ evaluation, cards, delta }: Props) {
  const r = evaluation.readiness;

  const applicableTotal = r.counters.applicable;
  const visibleApplicable = cards.filter(
    (c) => c.applicabilityCategory === "applicable" && c.lifecycleState !== "PLACEHOLDER",
  ).length;
  const placeholderApplicable = Math.max(0, applicableTotal - visibleApplicable);

  // Signal impact: unique affected rules and impacted-card count from
  // linkedSignals. `readinessChanged` is provisional until the diff store
  // lands — we report "Pending baseline" instead of guessing.
  const affectedRules = uniq(evaluation.linkedSignals.map((l) => l.ruleId)).length;
  const applicableIds = new Set(
    cards.filter((c) => c.applicabilityCategory === "applicable").map((c) => c.id),
  );
  const impactedCards = uniq(
    evaluation.linkedSignals.map((l) => l.ruleId).filter((rid) => applicableIds.has(rid)),
  ).length;
  const reviewRequired = (evaluation.signalBundle?.count ?? 0) > 0;

  return (
    <section
      data-testid="readiness-overview"
      aria-labelledby="readiness-overview-title"
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <header style={{ marginBottom: "var(--space-12)" }}>
        <h3
          id="readiness-overview-title"
          className="type-h3"
          style={{ margin: 0, fontSize: 13 }}
        >
          Readiness · analysis
        </h3>
        <p
          className="type-caption"
          style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}
        >
          The five independent tracks are in the top ribbon and the applicable /
          hidden / needs-config counts are in the scope strip — not repeated here.
          Below: count reconciliation, signal impact, and the ABCDPU × timing heatmap.
        </p>
        <p
          data-testid="overview-counting-note"
          className="type-caption"
          style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}
        >
          Five-way counts include all applicable cards. Lifecycle board and heatmap show visible applicable cards and exclude placeholder / curation-gap cards.
        </p>
        <div
          data-testid="readiness-reconciliation-strip"
          style={{
            marginTop: "var(--space-8)",
            padding: "var(--space-8)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-4)",
            background: "var(--color-surface-sunken)",
            color: "var(--color-text-secondary)",
            fontSize: 11,
            display: "grid",
            gap: 4,
          }}
        >
          <div>
            Applicable total {applicableTotal} = hard_law {r.tracks.legal.applicable} + evidence{" "}
            {r.tracks.evidence_standard.applicable} + market {r.tracks.market_force.applicable} + ecosystem{" "}
            {r.tracks.ecosystem.applicable}
          </div>
          <div>
            Visible applicable {visibleApplicable} = applicable total {applicableTotal} - placeholder{" "}
            {placeholderApplicable}
          </div>
        </div>
      </header>

      <div style={{ display: "grid", gap: "var(--space-12)" }}>
        {/* Signal impact audit */}
        <section data-testid="overview-signal-audit" style={panelStyle}>
          <h4 style={panelTitle}>Signal impact</h4>
          <dl style={{ margin: 0, fontSize: 11, display: "grid", rowGap: 4 }}>
            <Row label="Bundle">
              {evaluation.signalBundle ? (
                <span style={{ fontFamily: "var(--font-mono)" }}>{evaluation.signalBundle.bundleId}</span>
              ) : (
                <span style={{ color: "var(--color-text-secondary)" }}>(none ingested)</span>
              )}
            </Row>
            <Row label="Affected rules">
              <strong data-testid="impact-affected-rules">{affectedRules}</strong>
            </Row>
            <Row label="Impacted applicable cards">
              <strong data-testid="impact-impacted-cards">{impactedCards}</strong>
            </Row>
            <Row label="Review required">
              <strong data-testid="impact-review-required">{reviewRequired ? "Yes" : "No"}</strong>
            </Row>
            <Row label="Readiness Δ">
              {!delta ? (
                <span
                  data-testid="readiness-delta-baseline"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  建立基线中（首次评估）
                </span>
              ) : !delta.hasChange ? (
                <span
                  data-testid="readiness-delta-none"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  无变化
                </span>
              ) : (
                <strong
                  data-testid="readiness-delta"
                  style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}
                >
                  {summarizeReadinessDelta(delta)}
                </strong>
              )}
            </Row>
          </dl>
        </section>

        {/* Heatmap — the one 2-D analysis view */}
        <section data-testid="overview-heatmap-wrap" style={panelStyle}>
          <LifecycleTimingHeatmap cards={cards} />
        </section>
      </div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-8)" }}>
      <dt style={{ color: "var(--color-text-secondary)" }}>{label}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </div>
  );
}

function uniq<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

const panelStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-4)",
  padding: "var(--space-8)",
};
const panelTitle: React.CSSProperties = {
  margin: "0 0 var(--space-8) 0",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--color-text-secondary)",
};
